import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import { INLINE_MENTION } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import type { PaletteDef } from '@gitbook/react-uikit';
import type { TextRange } from '@gitbook/slate';
import type { MarkProps } from '@gitbook/slate-react';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    type CreateInputContentRefPaletteOptions,
    createInputContentRefPalette,
    useOptionalContentReferenceContext,
} from '../../ContentReferences';
import { SelectionPalettePicker } from '../common';
import { ContentEditorContext } from '../contexts';

/**
 * Component being rendered around the temporary mark created when typing a mention trigger.
 */
export function MentionPicker(
    props:
        | (MarkProps & {
              options: CreateInputContentRefPaletteOptions<ContentRefKind[]>;
          })
        | (MarkProps & {
              getPalette: (
                  markData: { symbol: string; value: string },
                  getCurrentRange: () => TextRange
              ) => PaletteDef;
          })
): React.ReactElement {
    const { editor } = props;
    const selectionPalettePickerProps =
        'getPalette' in props
            ? (({ getPalette, ...rest }) => rest)(props)
            : (({ options, ...rest }) => rest)(props);

    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    // Only the content-reference branch below needs it. A caller passing its own palette — the
    // agent composer's prompt palette, rendered in the application frame — has no content context,
    // and demanding one there took the composer down with the error boundary.
    const contentReferenceContext = useOptionalContentReferenceContext();
    const hiveClient = useHiveClient();

    const getPalette = React.useCallback(
        (markData: { symbol: string; value: string }, getCurrentRange: () => TextRange) => {
            if ('getPalette' in props) {
                return props.getPalette(markData, getCurrentRange);
            }

            const { options } = props;
            // Content-reference mentions are only ever registered where a space is in context, so
            // the provider is present whenever this branch runs.
            assertIsDefined(
                contentReferenceContext,
                'A content-reference mention needs a ContentReferenceContextProvider above it'
            );

            const replaceWithMention = (reference: api.ContentRef) => {
                editor.change((change) => {
                    change
                        .insertInlineAtRange(getCurrentRange(), {
                            type: INLINE_MENTION,
                            data: { ref: reference },
                            isVoid: true,
                        })
                        .focus();
                });

                logAnalyticsEvent({
                    type: 'edit_page_document_insert_inline',
                    inlineType: 'mention',
                    from: 'keyboard-shortcut',
                    kind: reference.kind,
                });
            };

            return createInputContentRefPalette(
                replaceWithMention,
                {
                    ...contentReferenceContext,
                    document: editor.value.document,
                },
                hiveClient,
                {
                    ...options,
                    // The mention palette is using the text value typed in the editor for search
                    search: { enabled: false },
                }
            );
        },
        [contentReferenceContext, editor, hiveClient, logAnalyticsEvent, props]
    );

    return <SelectionPalettePicker {...selectionPalettePickerProps} getPalette={getPalette} />;
}
